import { observer } from 'mobx-react-lite'
import { ReactElement, useMemo } from 'react'
import styled from 'styled-components'
import { I18n, Translate2 } from 'common'
import { useResultsStore } from 'results'
import { defaultTheme } from '../../../theme'
import { getAppliedFilterChips } from '../getAppliedFilterChips'
import { useSearchFilterActions } from '../useSearchFilterActions'
import { FilterChip } from './FilterChip'

import type { Filters } from 'src/store/models/SearchModels'
import type { AppliedFilterChipDescriptor } from '../types'

interface FilterChipBarProps {
  filtersOverride?: Filters
  onRemoveChip?: (chip: AppliedFilterChipDescriptor) => void
  onClearAll?: () => void
  compact?: boolean
}

export const FilterChipBar = observer(
  ({
    filtersOverride,
    onRemoveChip,
    onClearAll,
    compact = false,
  }: FilterChipBarProps = {}): ReactElement | null => {
    const { filters: storeFilters } = useResultsStore()
    const { removeChip: storeRemoveChip, clearAllFilters: storeClearAll } =
      useSearchFilterActions()

    const filters = filtersOverride ?? storeFilters
    const removeChip = onRemoveChip ?? storeRemoveChip
    const clearAll = onClearAll ?? storeClearAll

    const chips = useMemo(() => getAppliedFilterChips(filters), [filters])

    if (chips.length === 0) {
      return null
    }

    const showClearLink = chips.length > 1

    return (
      <Bar $compact={compact}>
        <ChipList aria-label={Translate2('appliedFilters')}>
          {chips.map((chip) => (
            <ChipListItem key={chip.id}>
              <FilterChip
                label={chip.label}
                onRemove={() => removeChip(chip)}
              />
            </ChipListItem>
          ))}
        </ChipList>
        {showClearLink && (
          <ClearButton type="button" onClick={clearAll}>
            <I18n name="clear" />
          </ClearButton>
        )}
      </Bar>
    )
  }
)

const Bar = styled.div<{ $compact: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-bottom: ${(p) => (p.$compact ? '0' : '12px')};
  margin-top: ${(p) => (p.$compact ? '16px' : '0')};
`

const ChipList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
`

const ChipListItem = styled.li`
  display: flex;
`

const ClearButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-family: Roboto, serif;
  font-size: 14px;
  line-height: 24px;
  color: ${defaultTheme.colors.blueLight};
  text-transform: capitalize;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px dashed ${defaultTheme.colors.blueLight};
    outline-offset: 2px;
  }
`
